<template>
  <div class="upload-progress bg-transparent">
    <div class="upload-status">
      <div class="flex items-center justify-between mb-2">
        <span class="text-base font-medium">{{ fileName }}</span>
        <div class="flex items-center gap-2">
          <span class="text-sm">{{ formatProgress(overallProgress) }}%</span>
          <!-- <button v-if="uploadStatus === 'uploading'" @click="$emit('pause')" class="text-orange-500">
            <v-icon>mdi-pause</v-icon>
          </button> -->
          <button v-if="uploadStatus === 'paused'" @click="$emit('resume')" class="text-orange-500">
            <v-icon>mdi-play</v-icon>
          </button>
          <button v-if="uploadStatus === 'failed'" @click="$emit('retry')" class="text-orange-500">
            <v-icon>mdi-refresh</v-icon>
          </button>
        </div>
      </div>
      
      <v-progress-linear
        :model-value="overallProgress"
        color="#F06906"
        height="8"
        rounded
      >
        <template v-slot:default="{ value }">
          <div class="relative w-full">
            <div class="chunks-progress absolute top-0 left-0 w-full h-2 flex">
              <!-- <div
                v-for="chunk in chunks"
                :key="chunk.index"
                class="chunk-progress h-full"
                :class="getChunkClass(chunk.status)"
                :style="{ width: `${100/totalChunks}%` }"
              ></div> -->
            </div>
          </div>
        </template>
      </v-progress-linear>

      <div class="flex justify-between mt-2 text-xs text-neutral-500">
        <span>{{ formatSize(uploadedSize) }} / {{ formatSize(fileSize) }}</span>
        <span>{{ uploadStatusText }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ChunkState } from '~/stores/uploadStore';

const props = defineProps<{
  fileName: string;
  fileSize: number;
  chunks: ChunkState[];
  totalChunks: number;
  overallProgress: number;
  uploadStatus: string;
}>();

const emit = defineEmits<{
  (e: 'pause'): void;
  (e: 'resume'): void;
  (e: 'retry'): void;
}>();

const uploadedSize = computed(() => {
  return props.chunks.reduce((sum, chunk) => {
    if (chunk.status === 'completed') {
      return sum + (chunk.end - chunk.start);
    } else if (chunk.status === 'uploading') {
      const chunkSize = chunk.end - chunk.start;
      return sum + (chunkSize * chunk.progress / 100);
    }
    return sum;
  }, 0);
});

const uploadStatusText = computed(() => {
  const statusMap = {
    'idle': 'ready',
    'uploading': 'uploading',
    'paused': 'paused',
    'completed': 'completed',
    'failed': 'failed'
  };
  return statusMap[props.uploadStatus] || props.uploadStatus;
});

const formatProgress = (progress: number): string => {
  return progress.toFixed(1);
};

const formatSize = (bytes: number): string => {
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

const getChunkClass = (status: ChunkState['status']): string => {
  const classes = {
    'pending': 'bg-gray-500 opacity-30',
    'uploading': 'bg-orange-500 animate-pulse',
    'completed': 'bg-orange-500',
    'failed': 'bg-red-500'
  };
  return classes[status] || '';
};
</script>

<style scoped>
.upload-progress {
  @apply bg-neutral-900 rounded-lg p-4 w-full max-w-[740px] mx-auto;
}

.chunk-progress {
  @apply transition-all duration-300;
}

.chunks-progress {
  gap: 2px;
}
</style> 